import { useState, useCallback } from "react";
import { Plus, Trash2, MessageSquare, ArrowLeft, Save, Clock, Zap } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db, auth } from "../lib/firebase";
import { collection, query, where, getDocs, doc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";

interface SequenceMessage {
  timing: "immediate" | "delay";
  delayMinutes?: number;
  content: string;
}

interface Sequence {
  id?: string;
  name: string;
  description: string;
  messages: SequenceMessage[];
  triggerOnNewLead: boolean;
  triggerOnStage?: string;
  createdAt: number;
  updatedAt: number;
  ownerId: string;
}

export function AutoFollowUp() {
  const [editingSequence, setEditingSequence] = useState<Sequence | null>(null);
  const queryClient = useQueryClient();

  const { data: sequences = [], isLoading } = useQuery<Sequence[]>({
    queryKey: ["sequences"],
    queryFn: async () => {
      if (!auth.currentUser) return [];
      const q = query(
        collection(db, "sequences"),
        where("ownerId", "==", auth.currentUser.uid)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Sequence));
    },
    enabled: !!auth.currentUser,
    refetchInterval: 5000,
  });

  const saveMutation = useMutation({
    mutationFn: async (seq: Sequence) => {
      if (!auth.currentUser) return;
      const now = Date.now();
      if (seq.id) {
        const ref = doc(db, "sequences", seq.id);
        await updateDoc(ref, { ...seq, updatedAt: now });
      } else {
        const id = crypto.randomUUID();
        const ref = doc(db, "sequences", id);
        await setDoc(ref, {
          ...seq,
          id,
          createdAt: now,
          updatedAt: now,
          ownerId: auth.currentUser.uid,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      setEditingSequence(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, "sequences", id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
    },
  });

  const handleSave = useCallback(() => {
    if (editingSequence) {
      saveMutation.mutate(editingSequence);
    }
  }, [editingSequence, saveMutation]);

  const handleAddMessage = useCallback(() => {
    if (!editingSequence) return;
    setEditingSequence({
      ...editingSequence,
      messages: [
        ...(editingSequence.messages || []),
        { timing: "immediate" as const, content: "" },
      ],
    });
  }, [editingSequence]);

  const handleRemoveMessage = useCallback((idx: number) => {
    if (!editingSequence) return;
    const newMsgs = [...editingSequence.messages];
    newMsgs.splice(idx, 1);
    setEditingSequence({ ...editingSequence, messages: newMsgs });
  }, [editingSequence]);

  const handleMessageChange = useCallback(
    (idx: number, field: keyof SequenceMessage, value: string | number) => {
      if (!editingSequence) return;
      const newMsgs = [...editingSequence.messages];
      newMsgs[idx] = { ...newMsgs[idx], [field]: value };
      setEditingSequence({ ...editingSequence, messages: newMsgs });
    },
    [editingSequence]
  );

  const handleInsertVariable = useCallback((idx: number, tag: string) => {
    if (!editingSequence) return;
    const newMsgs = [...editingSequence.messages];
    newMsgs[idx].content = (newMsgs[idx].content || "") + " " + tag;
    setEditingSequence({ ...editingSequence, messages: newMsgs });
  }, [editingSequence]);

  if (editingSequence) {
    return (
      <div className="flex flex-col h-full bg-slate-50 overflow-y-auto w-full p-6">
        <div className="max-w-3xl mx-auto w-full space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setEditingSequence(null)}
                className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h1 className="text-2xl font-bold text-slate-900">
                {editingSequence.id ? "Edit Sequence" : "New Sequence"}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditingSequence(null)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saveMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border shadow-sm space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sequence Name
              </label>
              <input
                type="text"
                value={editingSequence.name || ""}
                onChange={(e) =>
                  setEditingSequence({
                    ...editingSequence,
                    name: e.target.value,
                  })
                }
                className="w-full border-slate-200 rounded-lg border px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                placeholder="e.g. Welcome Series"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={editingSequence.description || ""}
                onChange={(e) =>
                  setEditingSequence({
                    ...editingSequence,
                    description: e.target.value,
                  })
                }
                className="w-full border-slate-200 rounded-lg border px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
                placeholder="Brief description of this sequence"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={editingSequence.triggerOnNewLead || false}
                onChange={(e) =>
                  setEditingSequence({
                    ...editingSequence,
                    triggerOnNewLead: e.target.checked,
                  })
                }
                className="text-green-600 focus:ring-green-500 rounded"
              />
              Trigger on new lead
            </label>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-800">
              Messages Timeline
            </h2>

            {(editingSequence.messages || []).map((msg, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-xl border shadow-sm relative"
              >
                <button
                  className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition-colors p-1"
                  onClick={() => handleRemoveMessage(idx)}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </div>
                  <h3 className="font-medium text-slate-900">
                    Message {idx + 1}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="radio"
                        name={`timing${idx}`}
                        checked={msg.timing === "immediate"}
                        onChange={() =>
                          handleMessageChange(idx, "timing", "immediate")
                        }
                        className="text-green-600 focus:ring-green-500"
                      />
                      <Zap className="w-4 h-4 text-slate-400" />
                      Immediate
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input
                        type="radio"
                        name={`timing${idx}`}
                        checked={msg.timing === "delay"}
                        onChange={() =>
                          handleMessageChange(idx, "timing", "delay")
                        }
                        className="text-green-600 focus:ring-green-500"
                      />
                      <Clock className="w-4 h-4 text-slate-400" />
                      After delay
                    </label>
                  </div>

                  {msg.timing === "delay" && (
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        Delay (minutes)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={msg.delayMinutes || 60}
                        onChange={(e) =>
                          handleMessageChange(
                            idx,
                            "delayMinutes",
                            parseInt(e.target.value, 10)
                          )
                        }
                        className="w-32 border-slate-200 rounded-lg border px-3 py-2 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-sm transition-all"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Message Content
                    </label>
                    <textarea
                      className="w-full h-32 border-slate-200 rounded-lg border px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 resize-none text-sm transition-all"
                      value={msg.content || ""}
                      onChange={(e) =>
                        handleMessageChange(idx, "content", e.target.value)
                      }
                      placeholder="Type your message here..."
                    />
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <span className="text-xs text-slate-500">Variables:</span>
                      {["{{+lead_name}}", "{{+user_name}}", "{{+org_name}}"].map(
                        (tag) => (
                          <button
                            key={tag}
                            className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md hover:bg-slate-200 transition-colors"
                            onClick={() => handleInsertVariable(idx, tag)}
                          >
                            {tag}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={handleAddMessage}
              className="w-full py-4 border-2 border-dashed border-green-300 text-green-700 font-medium rounded-xl hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" /> Add Message Step
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 p-6 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Auto Follow-up Sequences
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage automated drip campaigns triggered by lead behavior.
            </p>
          </div>
          <button
            onClick={() =>
              setEditingSequence({
                name: "New Sequence",
                description: "",
                messages: [],
                triggerOnNewLead: false,
                createdAt: 0,
                updatedAt: 0,
                ownerId: auth.currentUser?.uid || "",
              })
            }
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add New Sequence
          </button>
        </div>

        {isLoading ? (
          <div className="text-center p-12 text-slate-500 bg-white rounded-xl border">
            Loading sequences...
          </div>
        ) : sequences.length === 0 ? (
          <div className="text-center p-12 text-slate-500 bg-white rounded-xl border">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium mb-1">No sequences found</p>
            <p className="text-sm">Create one to get started with automated follow-ups.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sequences.map((seq) => (
              <div
                key={seq.id}
                className="bg-white border rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group relative"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (seq.id) deleteMutation.mutate(seq.id);
                  }}
                  className="absolute top-4 right-4 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div onClick={() => setEditingSequence(seq)}>
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-green-600 transition-colors pr-8">
                    {seq.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 mb-4 h-10 line-clamp-2">
                    {seq.description || "No description"}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-slate-50 px-3 py-1.5 rounded-full w-max">
                    <MessageSquare className="w-4 h-4 text-slate-400" />
                    {seq.messages?.length || 0} messages
                  </div>
                  {seq.triggerOnNewLead && (
                    <span className="mt-2 inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      Auto-trigger on new leads
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
